// frontend/src/pages/admin/TicketTypes.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Modal, Select } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css';
import TicketTypeForm from './TicketTypeForm';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;
const { Option } = Select;

const TicketTypes = () => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [deletingId, setDeletingId] = useState(null);

  const { appSettings } = useAppContext();
  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchTicketTypes = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchTicketTypesLoading';
    const hideLoading = showLoading(loadingKey, 'Loading ticket types...');

    try {
      const params = {
        sort: sortColumn,
        order: sortOrder,
      };

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/ticket-types', { params });
      let fetchedTypes = response.data.data || [];

      if (!Array.isArray(fetchedTypes)) {
        console.warn('TicketTypes.js: API response for ticket types was not an array. Assuming empty array.');
        fetchedTypes = [];
      }

      const mappedTypes = fetchedTypes.map(type => ({
        id: type.id,
        name: type.name,
        createdAt: type.createdAt,
        updatedAt: type.updatedAt,
        deletedAt: type.deleted_at || type.deletedAt,
      }));

      mappedTypes.sort((a, b) => {
        if (a.id === b.id) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return sortOrder === 'DESC' ? b.id - a.id : a.id - b.id;
      });

      setTicketTypes(mappedTypes);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Ticket Types Loaded', 'Ticket type list fetched successfully!', 'fetchTicketTypesSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching ticket types:', error);
      showError('Failed to Fetch', error.response?.data?.message || 'Error loading ticket types');
    } finally {
      setLoading(false);
    }
  }, [searchText, sortColumn, sortOrder, showLoading, showSuccess, showError, dateFormat, timeFormat]);

  useEffect(() => {
    fetchTicketTypes();
  }, [fetchTicketTypes, searchText]); // Added searchText as dependency

  // Add client-side filtering
  const filteredTicketTypes = (ticketTypes || []).filter(type => {
    return (type.name || '').toLowerCase().includes(searchText.toLowerCase());
  });

  const handleAddTicketType = () => {
    setEditingTicketType(null);
    setIsModalVisible(true);
  };

  const handleEditTicketType = (type) => {
    setEditingTicketType(type);
    setIsModalVisible(true);
  };

  const handleHardDelete = async (id, name) => {
    setDeletingId(id);
    const loadingKey = `deleteTicketType-${id}`;
    const hideLoading = showLoading(loadingKey, `Deleting "${name}"...`);

  const filteredTicketTypes = (ticketTypes || []).filter(type => {
    return (type.name || '').toLowerCase().includes(searchText.toLowerCase());
  });

    try {
      await api.delete(`/api/ticket-types/${id}`);
      hideLoading();
      showSuccess('Type Deleted', `Ticket type "${name}" deleted successfully!`, 'deleteTicketTypeSuccess');
      fetchTicketTypes(true);
    } catch (error) {
      hideLoading();
      console.error('Error deleting ticket type:', error.response?.data || error.message, error);
      
      let errorMessage = error.response?.data?.message || `Failed to delete ticket type "${name}".`;
      if (error.response?.status === 409) {
        errorMessage += ' This type is linked to existing tickets and cannot be deleted.';
      }

      showError('Deletion Failed', errorMessage, `deleteTicketTypeError-${id}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (currentSorter && currentSorter.field && currentSorter.order) {
      const order = currentSorter.order === 'ascend' ? 'ASC' : 'DESC';
      setSortColumn(currentSorter.field);
      setSortOrder(order);
    } else {
      // Default to ID descending when no sort is selected
      setSortColumn('id');
      setSortOrder('DESC');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      defaultSortOrder: 'descend',
      width: '8%',
    },
    {
      title: 'Type Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: '25%',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
      width: '20%',
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => {
        const dateA = a.updatedAt ? dayjs(a.updatedAt).valueOf() : 0;
        const dateB = b.updatedAt ? dayjs(b.updatedAt).valueOf() : 0;
        return dateA - dateB;
      },
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTicketType(record)}
            aria-label={`Edit ${record.name}`}
            disabled={record.deletedAt}
          />

          <Popconfirm
            title={`Are you sure you want to permanently delete "${record.name}"?`}
            description="This action cannot be undone and will fail if tickets are linked to this type."
            onConfirm={() => handleHardDelete(record.id, record.name)}
            okText="Yes, Delete"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete ${record.name}`}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ticket-types-page">
      <div className="ticket-types-header">
        <h2>Ticket Type Management</h2>
        <div className="ticket-types-actions">
          <Space size="middle">
            <Search
            placeholder="Search types..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => {
                setSearchText(e.target.value);
                if (e.target.value === '') {
                fetchTicketTypes(true);
                }
            }}
            onSearch={() => fetchTicketTypes(true)}
            style={{ width: 300 }}
            />

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTicketType}
              size="large"
            >
              Add Type
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredTicketTypes}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
        onChange={handleTableChange}
      />

      <TicketTypeForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchTicketTypes(true);
        }}
        ticketType={editingTicketType}
      />
    </div>
  );
};

export default TicketTypes;